import Observer, {IObserver, IObserverOptionsCommon} from '~/utils/observer'
import {IConditionalRuleSetRevive} from '~/interfaces/reviveInterfaces'
import {StringHelper} from '~/helpers/stringHelper'
import {IAnyConditionalRule} from '~/interfaces/formSubmissionEditInterfaces'
import FormulaObserver, {IFormulaObserver, IFormulaObserverOptions} from '~/utils/observers/formulaObserver'
import Guid from '~/helpers/guid'

type IRuleOrSetCallback = (params: { ruleSet: IConditionalRuleSetRevive } | { rule: IAnyConditionalRule }) => void

export interface IRulesObserverOptions<M> extends IObserverOptionsCommon<M> {
  fieldName: keyof M & string
}

export interface IRulesObserver<M> extends IObserver<M> {
  fieldName: keyof M & string
  formula: string | null
  ruleSets: Array<IConditionalRuleSetRevive>
  type: 'rules'

  createFormulaObserver(opts: Omit<IFormulaObserverOptions<M>, 'id' | 'model' | 'callback'>): IFormulaObserver<M>

  forEachRuleAndSet(callback: IRuleOrSetCallback): void
}

export default class RulesObserver<M> extends Observer<M, IRulesObserverOptions<M>> implements IRulesObserver<M> {
  readonly type: 'rules' = 'rules'

  constructor(protected options: IRulesObserverOptions<M>) {
    super(options)
  }

  get fieldName(): keyof M & string {
    return this.call("fieldName")
  }

  get ruleSets(): Array<IConditionalRuleSetRevive> {
    const ruleKey = `sorted${StringHelper.capitalize(StringHelper.toCamel(this.fieldName))}ConditionalRuleSets`
    return this.model[ruleKey] || []
  }

  get formula(): string | null {
    return this.model[this.formulaNameFrom(this.fieldName)]
  }

  forEachRuleAndSet(callback: IRuleOrSetCallback) {
    for (const ruleSet of this.ruleSets) {
      this.forEachRuleSet(ruleSet, callback)
    }
  }

  createFormulaObserver(opts: Omit<IFormulaObserverOptions<M>, 'id' | 'model' | 'callback'>) {
    const observer = new FormulaObserver({
      id: `${this.id} FormulaObserver ${Guid.create()}`,
      model: this.model,
      callback: this.callback,
      ...opts,
    })
    this.observers[observer.id] = observer
    return observer
  }

  private forEachRuleSet(ruleSet: IConditionalRuleSetRevive, callback: IRuleOrSetCallback): void {
    callback({ ruleSet })
    ruleSet.sortedConditionalRules.forEach(ruleOrSet => {
      if ('conditional_rules' in ruleOrSet) {
        this.forEachRuleSet(ruleOrSet, callback)
      } else {
        callback({ rule: ruleOrSet })
      }
    })
  }
}